// @givmo/compliance — the first visit: nothing recorded yet (CCM-R1-INTAKE (a); frames 01, 02).
//
// The ways in, peers, each with one verb; none required, and none needs AI (said once, in the
// subtitle). It stands in for the Calendar and for an empty Filings (copy ruling item 6), so the
// two never show an empty list under a header.
//
// ⚠ "Connect my AI" is HIDDEN (design go D7: "Hide it for now"): there is no connect flow to open
// yet, and a card that opened nothing would promise one. It comes back with that flow.
//
// CCM-MOD5B-FIRST-VISIT-DOOR (D-CCM-97, D1): the organization's registered agents and foreign
// qualifications take frame 01's third place, so the column is frame 01's width again, its three
// cards each frame 01's.
//
// A way in shows its button only when the screen hands it something to open: the add sheet and the
// upload sheet (slices 6 and 7). A button that opened nothing would be a broken affordance.

import type { ReactNode } from 'react';
import { Button, Card, Icon, testIdProp } from '@givmo/design-system';
import { Landmark, PencilLine, Upload } from 'lucide-react';
import { ADD_A_RECORD, RECORDS_DOOR_TITLE, RECORDS_SECTION_LINE } from './compliance-vendor-model';

const PREFIX = 'compliance-first-visit';

function WayIn({
  icon,
  title,
  body,
  action,
  primary = false,
  testId,
}: {
  icon: typeof PencilLine;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
  /** The release-1 spine: the way in that produces a calendar today. */
  primary?: boolean;
  testId: string;
}) {
  return (
    <Card className="flex flex-col gap-16 p-20" testId={testId}>
      <span className="flex size-36 items-center justify-center rounded-lg bg-neutral-100 text-icon">
        <Icon icon={icon} size={18} aria-hidden />
      </span>
      <div className="flex flex-col gap-6">
        <h2 className="text-s15 font-w600 text-text-strong">{title}</h2>
        <p className="text-s13 leading-h15 text-text-secondary">{body}</p>
      </div>
      {action !== undefined && (
        <div className="mt-auto">
          <Button
            appearance="saas"
            tone={primary ? 'primary' : 'secondary'}
            size="sm"
            onClick={action.onClick}
            testId={`${testId}-action`}
          >
            {action.label}
          </Button>
        </div>
      )}
    </Card>
  );
}

export interface FirstVisitProps {
  /** The page root it stands in for (the Calendar's or Filings'). */
  pageTestId: string;
  /** What is about the whole page, above the ways in (the Calendar's reminders notice). */
  notice?: ReactNode;
  /** Open the add sheet. */
  onAddFiling?: () => void;
  /** Open the upload sheet. */
  onUpload?: () => void;
  /** Open the add-record sheet (D2: V3's sheet). */
  onAddRecord?: () => void;
  /** What the host renders beside it (the sheets it opens). */
  children?: ReactNode;
  /** The review's way in, under the heading, while the review holds something open (frame 91). */
  review?: ReactNode;
}

export function FirstVisit({
  pageTestId,
  notice,
  onAddFiling,
  onUpload,
  onAddRecord,
  children,
  review,
}: FirstVisitProps) {
  return (
    <div
      className="mx-auto flex w-full max-w-[1000px] flex-col gap-32 px-16 pt-36 pb-48 sm:px-24 sm:pt-[72px]"
      {...testIdProp(pageTestId)}
    >
      {notice}
      <div className="flex flex-col gap-8 sm:items-center sm:text-center" {...testIdProp(PREFIX)}>
        <h1 className="text-s24 font-w700 text-text-strong">Bring in what you already have</h1>
        <p className="max-w-[560px] text-s15 leading-h15 text-text-secondary">
          Use any of these, in any order. None is required, and nothing here needs AI.
        </p>
      </div>
      {review}
      <div className="grid gap-16 md:grid-cols-2 lg:grid-cols-3">
        <WayIn
          icon={PencilLine}
          title="Add what I already know"
          body="Enter the filings you make, with your registration numbers, dates and the confirmations you have."
          {...(onAddFiling !== undefined
            ? { action: { label: 'Add a filing', onClick: onAddFiling } }
            : {})}
          primary
          testId={`${PREFIX}-add`}
        />
        <WayIn
          icon={Upload}
          title="Upload what I have"
          body="Past registrations, returns, bylaws and agency letters. We keep the originals; reading them comes later."
          {...(onUpload !== undefined
            ? { action: { label: 'Upload documents', onClick: onUpload } }
            : {})}
          testId={`${PREFIX}-upload`}
        />
        <WayIn
          icon={Landmark}
          title={RECORDS_DOOR_TITLE}
          body={RECORDS_SECTION_LINE}
          {...(onAddRecord !== undefined
            ? { action: { label: ADD_A_RECORD, onClick: onAddRecord } }
            : {})}
          testId={`${PREFIX}-records`}
        />
      </div>
      {children}
    </div>
  );
}
